import { enableProdMode, NgModuleRef } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { logFeature } from '@office-start/telemetry-service';
import { KnownFeatureNames } from '@office-start/telemetry-types';

import { DelayedServiceWorkerInitializer } from '../service-worker/delayed-service-worker-initializer.service';
import { ServiceWorkerInitializer } from '../service-worker/service-worker-initializer.service';
import { ServiceWorkerStateService } from '../service-worker/service-worker-state.service';
import { onAngularBootstrapError } from '../shared/bootstrap/bootstrap-handler';
import { AriaEventFactoryService } from '../shared/instrumentation/aria.service';
import { ClickLogging } from '../shared/instrumentation/click-logging.utility';
import { DispatcherInstrumentationService } from '../shared/instrumentation/dispatcher-instrumentation.service';
import { InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { UserProfileEventFactoryService } from '../shared/instrumentation/user-profile.service';
import { OfficeStartBootstrapperService } from '../shared/office-start/office-start-bootstrapper.service';
import { EventService } from '../shared/services/event.service';
import { OfficeRouteService } from '../shared/services/office-route.service';
import { KnownPerformanceMarks, PerformanceService } from '../shared/services/performance.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { PwaRootModule } from './pwa-root.module';
import { PwaDiagnosticsService } from './services/pwa-diagnostics.service';

import '../../less/HomePage.css';

if (!/localhost(:\d+)?$/.test(window.location.hostname)) {
    enableProdMode();
}

window.queuedMarks = window.queuedMarks || [];
window.performance.mark(KnownPerformanceMarks.AppBeginBootstrap);
window.queuedMarks.push({markName: KnownPerformanceMarks.AppBeginBootstrap, measureFrom: window.measureAppFrom});
platformBrowserDynamic()
    .bootstrapModule(PwaRootModule, {
        preserveWhitespaces: false //Needed for JiT Compilation to remove Whitespaces
    }).then((ngModuleRef: NgModuleRef<PwaRootModule>) => {
        window.performance.mark(KnownPerformanceMarks.AppCompleteBootstrap);
        window.queuedMarks.push({markName: KnownPerformanceMarks.AppCompleteBootstrap, measureFrom: KnownPerformanceMarks.AppBeginBootstrap});
        const rootInjector = ngModuleRef.injector;

        const startupConfig = rootInjector.get(ClientStartupConfigService);
        if (startupConfig.config.sharedClientStartupContext) {
            if (window.overrideCID) {
                startupConfig.config.sharedClientStartupContext.correlationId = window.overrideCID;
            }

            if (window.overrideSID) {
                startupConfig.config.sharedClientStartupContext.sessionId = window.overrideSID;
            }

            if (window.requestOriginOverride) {
                startupConfig.config.sharedClientStartupContext.requestOrigin = window.requestOriginOverride;
            }
        }

        const capabilitiesService = rootInjector.get(CapabilitiesService);
        const serviceWorkerStartup = rootInjector.get(capabilitiesService.capabilities.delayedServiceWorkerInit ? DelayedServiceWorkerInitializer : ServiceWorkerInitializer);
        serviceWorkerStartup.initialize();

        const routeService = rootInjector.get(OfficeRouteService);
        const serviceWorkerStateService = rootInjector.get(ServiceWorkerStateService);
        routeService.start();

        const instrumentationService = rootInjector.get(InstrumentationService);
        ClickLogging.setup(instrumentationService);

        const diagnosticService = rootInjector.get(PwaDiagnosticsService);
        diagnosticService.startTrackingDiagnostics();

        if (instrumentationService instanceof DispatcherInstrumentationService) {
            instrumentationService.registerOfficeRouteService(routeService);
            instrumentationService.registerServiceWorkerStateService(serviceWorkerStateService, true);
            const userProfileEventFactoryService = rootInjector.get(UserProfileEventFactoryService);
            const ariaEventFactoryService = rootInjector.get(AriaEventFactoryService);
            // TODO: Enable DocDB writes for MSA
            instrumentationService.registerEventFactories(startupConfig.config.authType === AuthType.MSA ? [ariaEventFactoryService] : [userProfileEventFactoryService, ariaEventFactoryService]);
        }
        if (startupConfig.config.sharedClientStartupContext.oTelEnabled) {
            const officeStartBootstrapperService = rootInjector.get(OfficeStartBootstrapperService);
            officeStartBootstrapperService.bootstrapOfficeStart();
        }
        instrumentationService.logGenericEvent(KnownInstrumentationEvents.PwaBootstrapEnd);
        logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.PwaBootstrapEnd });

        const performanceService = rootInjector.get(PerformanceService);

        // There are some inline scripts that get run before instrumentation service is available, so logs and perf markers are queued
        window.performanceService = performanceService;
        window.instrumentationService = instrumentationService;

        // Broadcast event indicating that app initialization is completed. This is when app UI can be safely populated
        // based on config data.
        const eventService = rootInjector.get(EventService);
        eventService.broadcastEvent({
            name: AngularEvents.appInitialized,
            args: []
        });

        const handlePrefetchers = async (northStarEnabled, edgeworthReactEnabled) => {
            if (edgeworthReactEnabled) {
                const officeStartBootstrapperService = rootInjector.get(OfficeStartBootstrapperService);
                officeStartBootstrapperService.bootstrapOfficeStart();

                const dataPrefetchModulePromise = import(/*webpackChunkName: "data-prefetch-service" */ '@office-start/data-prefetch-service');

                if (northStarEnabled) {
                    // prefetchNorthStar must execute before prefetchEdgeworthData
                    const northStarPrefetchModulePromise = import(/*webpackChunkName: "northstar-prefetch-service" */ '@office-start/northstar-prefetch-service');
                    northStarPrefetchModulePromise.then(northStarPrefetchMod => {
                        northStarPrefetchMod.prefetchNorthStar();
                        dataPrefetchModulePromise.then(dataPrefetchMod => {
                            dataPrefetchMod.prefetchEdgeworthData();
                        });
                    });
                } else {
                    dataPrefetchModulePromise.then(dataPrefetchMod => {
                        dataPrefetchMod.prefetchEdgeworthData();
                    });
                }
            }
        };

        handlePrefetchers(capabilitiesService.capabilities.northStarEnabled, capabilitiesService.capabilities.edgeworthReactEnabled);
    }).catch(error => {
        window.shellAndConfigResponse.then(response => {
            let apiResponse;
            try {
                // V2 API response is an object instead of string, so keeping the try catch block here while we transition
                apiResponse = JSON.parse(response);
            } catch {
                apiResponse = response;
            }

            if (apiResponse && apiResponse.clientStartupConfiguration) {
                onAngularBootstrapError(error, JSON.parse(apiResponse.clientStartupConfiguration));
            }
        });
    });
